import apiClient from "./apiClient";
import i18n from "../../i18n";

const getAcceptLanguage = () =>
  i18n.language?.includes("ru") ? "ru-RU" : "kk-KZ";

const headers = () => ({
  "Content-Type": "application/json",
  "Accept-Language": getAcceptLanguage(),
});

const BASE = () =>
  `${process.env.REACT_APP_URL_DOCUMENTS_SERVICE}/reports/marking-codes`;

export type MarkingCodesReportStatus = "FORMING" | "FORMED" | "ERROR";

export type MarkingCodesPackagingType = "CONSUMER" | "GROUP";

export type MarkingCodesKmStatus =
  | "ISSUED"
  | "INFLICTED"
  | "IN_CIRCULATION"
  | "WITHDRAWN_FROM_CIRCULATION"
  | "PRE_SALE"
  | "AGGREGATED"
  | "DEAGGREGATED";

export type MarkingCodesReportFilters = {
  productGroupId?: number;
  dateFrom?: string;
  dateTo?: string;
  statuses?: MarkingCodesKmStatus[];
  organizationId?: number;
  ntin?: string;
  productTypeCode?: string;
  packagingType?: MarkingCodesPackagingType;
  orderNumber?: string;
  regionCode?: number;
  districtCode?: number;
  cityCode?: number;
};

export type MarkingCodesReportFile = {
  format: "XLSX" | "PDF";
  fileName?: string;
  sizeBytes?: number;
};

export type MarkingCodesReportItem = {
  id: number;
  reportType?: string;
  reportNumber?: string;
  productGroupId?: number;
  filters?: MarkingCodesReportFilters;
  status: MarkingCodesReportStatus;
  requestedAt?: string;
  startedAt?: string;
  completedAt?: string;
  rowsCount?: number;
  errorMessage?: string;
  // Исполнитель отчёта: id и ФИО того, кто сформировал
  requestedBy?: number;
  requestedByName?: string;
  files?: MarkingCodesReportFile[];
};

export type MarkingCodesReportListResponse = {
  reports: MarkingCodesReportItem[];
  currentPage?: number;
  itemsPerPage?: number;
  totalItems?: number;
  totalPages?: number;
};

// Фильтры журнала (тело POST /list). Даты — yyyy-MM-dd, reportNumber — точное совпадение
export type MarkingCodesReportListParams = {
  status?: MarkingCodesReportStatus;
  reportNumber?: string;
  dateFrom?: string;
  dateTo?: string;
};

export const searchMarkingCodesReports = async (
  page: number = 1,
  size: number = 20,
  filters: MarkingCodesReportListParams = {},
): Promise<MarkingCodesReportListResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  // Пустые фильтры не отправляем
  const body = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => Boolean(value)),
  );

  const response = await apiClient.post(`${BASE()}/list?${params.toString()}`, body, {
    headers: headers(),
    withCredentials: true,
  });
  return response.data;
};

export const createMarkingCodesReport = async (
  payload: MarkingCodesReportFilters,
): Promise<MarkingCodesReportItem> => {
  const response = await apiClient.post(BASE(), payload, {
    headers: headers(),
    withCredentials: true,
  });
  return response.data;
};

export const getMarkingCodesReport = async (
  reportId: number,
): Promise<MarkingCodesReportItem> => {
  const response = await apiClient.get(`${BASE()}/${reportId}`, {
    headers: headers(),
    withCredentials: true,
  });
  return response.data;
};

export const downloadMarkingCodesReport = async (
  reportId: number,
  format: "XLSX" | "PDF",
): Promise<Blob> => {
  const response = await apiClient.get(
    `${BASE()}/${reportId}/download?format=${format}`,
    {
      headers: {
        "Accept-Language": getAcceptLanguage(),
      },
      withCredentials: true,
      responseType: "blob",
    },
  );
  return response.data;
};

export type MarkingCodeReportRow = {
  number?: number;
  organizationName?: string;
  organizationIinBin?: string;
  participantType?: string;
  regionCode?: string;
  districtCode?: string;
  cityCode?: string;
  code?: string;
  packagingType?: string;
  productTypeCode?: string;
  productName?: string;
  ntin?: string;
  ntinName?: string;
  status?: MarkingCodesKmStatus;
  orderDate?: string;
  orderNumber?: string;
  personalizationDate?: string;
  personalizationNumber?: string;
  withdrawalDate?: string;
  withdrawalBin?: string;
  withdrawalName?: string;
  transferActDate?: string;
  transferActNumber?: string;
  transferActReason?: string;
  transferActReceiverBin?: string;
  transferActReceiverName?: string;
  mainMaterial?: string;
  assay?: string;
  trademark?: string;
  weight?: number;
  size?: number;
  hornLength?: number;
  hornSort?: string;
  originSource?: string;
  woodType?: string;
  woodSpecies?: string;
  loggingTicketNumber?: string;
  woodVolume?: number;
};

export type MarkingCodesReportPreviewResponse = {
  rows: MarkingCodeReportRow[];
  totalItems?: number;
  totalPages?: number;
  page?: number;
  size?: number;
  includeOrganizationColumns?: boolean;
};

// Предпросмотр строк отчёта по тем же фильтрам, что и генерация файла (size — максимум 100)
export const previewMarkingCodesReport = async (
  filters: MarkingCodesReportFilters,
  page: number = 1,
  size: number = 20,
): Promise<MarkingCodesReportPreviewResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  const response = await apiClient.post(
    `${BASE()}/preview?${params.toString()}`,
    filters,
    {
      headers: headers(),
      withCredentials: true,
    },
  );
  return response.data;
};
